import { DataSyncResponse, NavigationDataResponse } from '@/api/navigationApi/types.gen'
import { UseQueryResult } from '@tanstack/react-query'

import { useEffect, useRef } from 'react'
import { Events } from './types'

export const didNavStatusChange = ({
  currentModuleState,
  lastModuleState,
  currentFreeride,
  lastFreeride,
}: {
  currentModuleState: string
  lastModuleState: string | null
  currentFreeride: boolean
  lastFreeride: boolean | null
}): boolean => currentModuleState !== lastModuleState || currentFreeride !== lastFreeride

/**
 * Feeds navigation and sync query data to the state machine.
 *
 * **Update intervals:**
 * - `NAV_UPDATE`: Sent when `route_module_state` or `is_freeride` changes OR every 500ms (whichever comes first).
 *   The underlying `navigationQuery` polls every 200ms, but updates are throttled here.
 * - `SYNC_UPDATE`: Sent only when sync status actually changes (e.g., Waiting → InProgress → Finished).
 *   The underlying `syncQuery` polls every 1000ms, but duplicate statuses are filtered.
 * - `NAV_ERROR` / `SYNC_ERROR`: Sent immediately when either query enters an error state.
 */

export const useNavigationMachineFeed = ({
  send,
  syncQuery,
  navigationQuery,
  shouldStartDropoff,
  secondsToDropoff,
  vehicleId,
}: {
  send: (event: Events) => void
  syncQuery: UseQueryResult<DataSyncResponse, Error>
  navigationQuery: UseQueryResult<NavigationDataResponse, Error>
  shouldStartDropoff: boolean
  secondsToDropoff: number
  vehicleId: string | undefined
}) => {
  const lastStateRef = useRef<string | null>(null)
  const lastFreerideRef = useRef<boolean | null>(null)
  const lastUpdateRef = useRef<number>(0)
  const lastSyncStatusRef = useRef<DataSyncResponse['status'] | null>(null)

  useEffect(() => {
    const data = navigationQuery.data
    if (!data) return
    // If vehicleId is provided (remote viewing), validate it matches. Otherwise accept any data (local mode).
    if (vehicleId && data.patrol_vehicle?.id !== vehicleId) return

    const now = Date.now()
    const currentModuleState = data.route_module_state
    const currentFreeride = data.is_freeride === true
    const statusChanged = didNavStatusChange({
      currentModuleState,
      lastModuleState: lastStateRef.current,
      currentFreeride,
      lastFreeride: lastFreerideRef.current,
    })

    if (statusChanged || now - lastUpdateRef.current > 500) {
      send({
        type: 'NAV_UPDATE',
        data,
        shouldStartDropoff,
        secondsToDropoff,
      })

      lastUpdateRef.current = now
      lastStateRef.current = currentModuleState
      lastFreerideRef.current = currentFreeride
    }
  }, [navigationQuery.data, vehicleId, shouldStartDropoff, secondsToDropoff, send])

  useEffect(() => {
    if (syncQuery.data && syncQuery.data.status !== lastSyncStatusRef.current) {
      lastSyncStatusRef.current = syncQuery.data.status
      send({
        type: 'SYNC_UPDATE',
        status: syncQuery.data.status,
        updatedAt: syncQuery.dataUpdatedAt,
      })
    }
  }, [syncQuery.data, syncQuery.dataUpdatedAt, send])

  useEffect(() => {
    if (navigationQuery.isError) send({ type: 'NAV_ERROR', error: navigationQuery.error })
    if (syncQuery.isError) send({ type: 'SYNC_ERROR', error: syncQuery.error })
  }, [navigationQuery.isError, navigationQuery.error, syncQuery.isError, syncQuery.error, send])
}
